import React, { useState } from 'react';

const FONT = '"Nunito", "Baloo 2", sans-serif';

/**
 * Shared MCQ layout for placement games that show a prompt + 4 clickable options.
 * Handles selection state, correct/wrong feedback, and auto-advance.
 *
 * Props:
 *  - promptContent: React node to render as the question prompt
 *  - options: [{ label: string, correct: boolean, image_url?: string }]
 *  - onAnswer: (isCorrect, selectedLabel) => void
 *  - palette: color palette
 *  - disabled: boolean
 *  - renderOption: (opt, idx, { selected, showResult }) => ReactNode (optional custom renderer)
 */
export default function MCQLayout({ promptContent, options, onAnswer, palette: P, disabled, renderOption }) {
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const handleClick = (idx) => {
    if (disabled || showResult || selected !== null) return;
    const opt = options[idx];
    const isCorrect = !!opt.correct;
    setSelected(idx);
    setShowResult(true);
    setTimeout(() => {
      onAnswer(isCorrect, opt.label || opt.image_url || '');
    }, isCorrect ? 700 : 1100);
  };

  const LABELS = ['A', 'B', 'C', 'D'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, width: '100%', maxWidth: 560, margin: '0 auto' }}>
      {/* Prompt area */}
      <div style={{ width: '100%', textAlign: 'center', padding: '20px 0' }}>
        {promptContent}
      </div>

      {/* Options grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, width: '100%' }}>
        {(options || []).map((opt, idx) => {
          const isSelected = selected === idx;
          const isCorrect = !!opt.correct;
          let bg = '#fff';
          let borderColor = P.ink;
          let shadow = `0 4px 0 ${P.ink}`;
          if (showResult && isSelected) {
            bg = isCorrect ? '#D4F5E0' : '#FFD6D6';
            borderColor = isCorrect ? '#2ECC71' : '#E74C3C';
            shadow = isCorrect ? '0 4px 0 #27AE60' : '0 4px 0 #C0392B';
          } else if (showResult && isCorrect) {
            bg = '#D4F5E0';
            borderColor = '#2ECC71';
          }

          if (renderOption) {
            return (
              <div key={idx} onClick={() => handleClick(idx)} style={{ cursor: disabled || showResult ? 'default' : 'pointer' }}>
                {renderOption(opt, idx, { selected, showResult, bg, borderColor, shadow })}
              </div>
            );
          }

          return (
            <button
              key={idx}
              onClick={() => handleClick(idx)}
              disabled={disabled || showResult}
              style={{
                fontFamily: FONT, fontSize: 17, fontWeight: 700,
                padding: '16px 14px', borderRadius: 14,
                border: `2.5px solid ${borderColor}`,
                boxShadow: shadow,
                background: bg, color: P.ink,
                cursor: disabled || showResult ? 'default' : 'pointer',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <span style={{
                width: 28, height: 28, borderRadius: '50%',
                background: isSelected ? borderColor : P.sand,
                color: isSelected ? '#fff' : P.ink,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800, flexShrink: 0,
              }}>
                {LABELS[idx]}
              </span>
              <span style={{ flex: 1, textAlign: 'left' }}>{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* Feedback */}
      {showResult && selected !== null && (
        <div style={{
          fontSize: 20, fontWeight: 800, fontFamily: FONT,
          color: options[selected]?.correct ? '#2ECC71' : '#E74C3C',
        }}>
          {options[selected]?.correct ? '✓ Đúng rồi!' : '✗ Sai rồi!'}
        </div>
      )}
    </div>
  );
}
